/**
 * Full Box Manager
 *
 * This class listens for cart updates and calculates whether the cart
 * contains full boxes based on product volumes and shipping configuration.
 * It maintains an internal state and manages special full box line items accordingly.
 *
 * Usage:
 * - Access current state: fullBoxManager.getState()
 * - Listen for state changes: subscribe('fullBoxStateChange', ({ hasFullBox }) => { ... })
 * - Or use native events: document.addEventListener('fullBoxStateChange', (e) => { console.log(e.detail.hasFullBox); })
 */
class FullBoxManager {
  constructor() {
    this.FULL_BOX_VARIANT_ID = window.fullBoxProduct;

    this.infoKey = window.fullBoxInfoKey || "";
    this.infoValue = window.fullBoxInfoValue || "";

    this.unsubscribe = null;
    this.hasFullBox = false; // Internal state to track full box status
    this.debug = false;
    this.isUpdating = false; // Flag to prevent recursive updates
    this.volumeCache = {}; // Cache product volumes to avoid repeated fetches - only cached within current browser session
  }

  /**
   * Initialize the full box manager
   */
  async init() {
    // Check if the full box variant ID exists
    if (!this.FULL_BOX_VARIANT_ID) {
      console.warn('Full box variant ID not configured. Full box functionality will not run.');
      return;
    }

    // Subscribe to cart updates
    this.unsubscribe = subscribe('cartUpdate', async (cartData) => {
      await this.handleCartUpdate(cartData);
    });

    // Run calculation on page load with current cart state
    await this.initializeOnLoad();

  }

  /**
   * Initialize and run full box calculation on page load
   */
  async initializeOnLoad() {
    try {
      // Fetch the current cart state
      const cartResponse = await fetch('/cart.js');
      const cartData = await cartResponse.json();

      // Process the cart data
      await this.handleCartUpdate(cartData);
    } catch (error) {
      this.debug && console.error('Error initializing full box calculation on load:', error);
    }
  }

  /**
   * Handle cart update events
   * @param {Object} cartData - The cart data from the update event
   */
  async handleCartUpdate(cartData) {
    // Prevent recursive calls
    if (this.isUpdating) {
      this.debug && console.log('Full box manager is already updating, skipping...');
      return;
    }

    try {
      this.isUpdating = true;

      // Calculate if cart has full boxes
      const hasFullBox = await this.calculateFullBoxes(cartData);

      // Update internal state
      const stateChanged = this.hasFullBox !== hasFullBox;
      this.hasFullBox = hasFullBox;

      // Publish state change event if state changed
      if (stateChanged) {
        this.publishStateChange(hasFullBox);
      }

      // Manage the full box line item (pass cart data to avoid refetch)
      await this.manageFullBoxLineItem(hasFullBox, cartData);
    } catch (error) {
      this.debug && console.error('Error in full box cart handler:', error);
    } finally {
      this.isUpdating = false;
    }
  }

  /**
   * Publish full box state change event
   * @param {boolean} hasFullBox - Whether the cart has full boxes
   */
  publishStateChange(hasFullBox) {
    // Publish event for other parts of the app to listen to
    if (typeof publish === 'function') {
      publish('fullBoxStateChange', { hasFullBox });
    }

    // Also dispatch a native DOM event
    const event = new CustomEvent('fullBoxStateChange', {
      detail: { hasFullBox },
      bubbles: true
    });
    document.dispatchEvent(event);

    this.debug && console.log('Full box state changed:', hasFullBox ? 'Has full box' : 'No full box');
  }

  /**
   * Calculate if cart has full boxes based on product volumes and shipping config
   * Items with no volume data are ignored in the calculation and don't prevent full box qualification
   * The full box product itself is excluded from all calculations
   * @param {Object} cartData - The cart data
   * @returns {Promise<boolean>} - Whether the cart has full boxes
   */
  async calculateFullBoxes(cartData) {
    // Check if shipping config is available
    if (window.customerB2B) {
      // B2B customers don't qualify for full box
      return false;
    }

    if (!window.shippingConfig || !cartData.items) {
      return false;
    }

    // Check if cart is empty or only contains the full box product
    const nonFullBoxItems = cartData.items.filter(item =>
      item.variant_id !== this.FULL_BOX_VARIANT_ID
    );

    // If cart is empty or only has the full box product, don't need to calculate full boxes
    if (nonFullBoxItems.length === 0) {
      return false;
    }

    // Fetch product pages to extract metafield data from HTML
    // Exclude the full box product itself from calculations
    const uniqueProducts = cartData.items.reduce((acc, item) => {
      // Skip the full box product variant
      if (item.variant_id === this.FULL_BOX_VARIANT_ID) {
        return acc;
      }

      if (!acc.find(p => p.handle === item.handle)) {
        acc.push({ id: item.product_id, handle: item.handle });
      }
      return acc;
    }, []);

    const productVolumes = await this.fetchProductVolumes(uniqueProducts);

    // Calculate cart lines grouped by volume
    const cartLines = this.calculateCartLines(cartData.items, productVolumes);

    // If no items have volume data, add the full box line item
    // (items with no volume don't contribute to calculation)
    if (Object.keys(cartLines).length === 0) {
      return true;
    }

    // Group cart lines according to shipping configuration
    const cartLineGroups = this.groupCartLines(cartLines);

    // Check if ALL groups have complete boxes (no remainders)
    const hasFullBox = cartLineGroups.length > 0 && cartLineGroups.every(group => !group.hasRemainder);

    return hasFullBox;
  }

  /**
   * Fetch product volumes from product pages
   * @param {Array} uniqueProducts - Array of unique products
   * @returns {Promise<Object>} - Object mapping product IDs to volumes
   */
  async fetchProductVolumes(uniqueProducts) {
    const productVolumes = {};

    for (const { id, handle } of uniqueProducts) {
      // Check cache first
      if (this.volumeCache[id]) {
        productVolumes[id] = this.volumeCache[id];
        continue;
      }

      try {
        // Fetch the product page HTML only if not cached
        const response = await fetch(`/products/${handle}`);

        if (response.ok) {
          const htmlText = await response.text();
          const volumeValue = this.extractVolumeFromHTML(htmlText, handle);

          if (volumeValue) {
            productVolumes[id] = volumeValue;
            // Cache the volume value
            this.volumeCache[id] = volumeValue;
          }
        }
      } catch (error) {
        // Silently continue if fetch fails
      }
    }

    return productVolumes;
  }

  /**
   * Extract volume from HTML content
   * @param {string} html - The HTML content
   * @param {string} handle - The product handle
   * @returns {number|null} - The extracted volume or null
   */
  extractVolumeFromHTML(html, handle) {
    // Create a temporary DOM element to parse the HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;

    // Look for the volume data attribute we added to main-product section
    const volumeElement = tempDiv.querySelector('[data-product-volume]');
    if (volumeElement) {
      const volume = volumeElement.dataset.productVolume;
      return parseFloat(volume);
    }

    // Fallback: Look for other possible volume indicators
    const volumeDataElement = tempDiv.querySelector('.product-volume-data[data-product-volume]');
    if (volumeDataElement) {
      const volume = volumeDataElement.dataset.productVolume;
      return parseFloat(volume);
    }

    return null;
  }

  /**
   * Calculate cart lines grouped by volume
   * Excludes the full box product from calculations
   * @param {Array} items - Cart items
   * @param {Object} productVolumes - Product volume mapping
   * @returns {Object} - Cart lines grouped by volume
   */
  calculateCartLines(items, productVolumes) {
    return items.reduce((acc, line) => {
      // Skip the full box product variant
      if (line.variant_id === this.FULL_BOX_VARIANT_ID) {
        return acc;
      }

      // Get volume from extracted HTML data
      const lineVolume = productVolumes[line.product_id] ?
        Math.floor(productVolumes[line.product_id]) : '';
      const lineQuantity = line.quantity;

      if (lineVolume) {
        acc[lineVolume] = (acc[lineVolume] || 0) + lineQuantity;
      }

      return acc;
    }, {});
  }

  /**
   * Group cart lines according to shipping configuration
   * @param {Object} cartLines - Cart lines grouped by volume
   * @returns {Array} - Grouped cart lines
   */
  groupCartLines(cartLines) {
    return Object.keys(cartLines).reduce((array, volume) => {
      const group = window.shippingConfig.find(config =>
        config?.group_volumes?.includes(parseInt(volume, 10))
      );

      if (!group) return array;

      const obj = {
        volume: group.group_volumes,
        quantity: cartLines[volume],
        boxCount: group.box_count,
        // Calculate if this group has any incomplete boxes (remainders)
        hasRemainder: (cartLines[volume] % group.box_count) !== 0
      };

      const existingGroup = array.find(v => v.volume.toString() === group.group_volumes.toString());
      if (existingGroup) {
        const existingRecordIndex = array.indexOf(existingGroup);
        const totalQuantity = existingGroup.quantity + obj.quantity;
        array[existingRecordIndex] = {
          ...existingGroup,
          quantity: totalQuantity,
          hasRemainder: (totalQuantity % obj.boxCount) !== 0
        };
      } else {
        array.push(obj);
      }

      return array;
    }, []);
  }


  /**
   * Manage the full box line item in the cart
   * @param {boolean} hasFullBox - Whether the cart has full boxes
   * @param {Object} cartData - Optional cart data to avoid refetching
   */
  async manageFullBoxLineItem(hasFullBox, cartData = null) {
    try {
      // Use provided cart data or fetch if not available
      let updatedCart = cartData;
      if (!cartData) {
        const cartResponse = await fetch('/cart.js');
        updatedCart = await cartResponse.json();
      }

      // Check if the full box variant is already in the cart
      let existingItem = updatedCart.items.find(item =>
        item.variant_id === this.FULL_BOX_VARIANT_ID
      );

      //check if the fullbox item exists and if it has the line item property "_full_box" set to "true". if it it doesn't have the property, remove this item from the cart so it can be re-added correctly
      if (existingItem && !existingItem.properties._full_box) {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: existingItem.key,
            quantity: 0
          })
        });
        existingItem = null;
      }

      // Safety check: if cart is empty or only has the full box product, ensure full box is removed
      const nonFullBoxItems = updatedCart.items.filter(item =>
        item.variant_id !== this.FULL_BOX_VARIANT_ID
      );


      if (nonFullBoxItems.length === 0 && existingItem) {
        // Cart is empty or only has full box product - hasFullBox should be false and the line item should be removed
        hasFullBox = false;
      }

      // Check if we actually need to make changes
      const needsToAdd = hasFullBox && !existingItem;
      const needsToRemove = !hasFullBox && existingItem;

      if (!needsToAdd && !needsToRemove) {
        // Cart is already in the correct state, no need to make changes
        this.debug && console.log('Full box line item already in correct state');
        return;
      }

      if (needsToAdd) {
        // Build properties object
        const properties = {
          _full_box: 'true'
        };

        // Add info property only if infoKey and infoValue are provided
        if (this.infoKey && this.infoValue) {
          properties[this.infoKey] = this.infoValue;
        }

        // Add the full box variant
        await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: this.FULL_BOX_VARIANT_ID,
            quantity: 1,
            properties: properties
          })
        });

        this.debug && console.log('Full box line item added');
      } else if (needsToRemove) {
        // Remove the full box variant by setting quantity to 0
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: existingItem.key,
            quantity: 0
          })
        });

        this.debug && console.log('Full box line item removed');
      }
    } catch (error) {
      this.debug && console.error('Error managing full box line item:', error);
    }
  }
}

// Initialize the full box manager and expose globally
const fullBoxManager = new FullBoxManager();
window.fullBoxManager = fullBoxManager;
fullBoxManager.init();
